/**
 * CookieConsent — calm, lawful, single-time banner.
 *
 * Renders once at the bottom of every page until the user picks an
 * option. Stores `accepted` / `essential-only` in localStorage so it
 * doesn't nag on every navigation.
 *
 * Distinct from the existing AnalyticsConsentBanner (which is opt-in
 * for de-identified research analytics). This one covers the
 * EU/UK/CA cookie + tracking-consent regimes that apply to anyone
 * who renders the SPA, not just consenting research participants.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const KEY = "maldek_cookie_consent_v1";

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const v = localStorage.getItem(KEY);
      if (!v) setVisible(true);
    } catch {
      // localStorage blocked — render the banner; the user can dismiss it.
      setVisible(true);
    }
  }, []);

  // Auto-dismiss after 12s with "essential only" so the banner never
  // blocks the UI for users who ignore it.
  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(() => decide("essential"), 12000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const decide = (choice) => {
    try {
      localStorage.setItem(KEY, choice);
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie preferences"
      data-testid="cookie-consent"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[100] w-[calc(100%-2rem)] sm:w-auto sm:max-w-xl"
    >
      <div
        className="rounded-2xl border border-[#6B8F71]/30 shadow-2xl px-5 py-4 backdrop-blur-md"
        style={{ background: "rgba(255, 253, 250, 0.97)" }}
      >
        <p className="text-sm leading-relaxed" style={{ color: "#000000" }}>
          We use a small set of cookies to keep you signed in and to remember
          your track. Optional, anonymous analytics help us find broken pages.{" "}
          <Link
            to="/cookies"
            className="underline decoration-dotted underline-offset-2"
            style={{ color: "#6B8F71" }}
          >
            Read more
          </Link>
          .
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            data-testid="cookie-consent-accept-all"
            onClick={() => decide("all")}
            className="px-4 py-1.5 rounded-full text-sm font-bold transition-all"
            style={{
              background: "linear-gradient(135deg, #6B8F71 0%, #6B8F71 100%)",
              color: "#2b261c",
              boxShadow: "0 6px 18px rgba(44, 90, 140, 0.30)",
            }}
          >
            Accept all
          </button>
          <button
            type="button"
            data-testid="cookie-consent-essential-only"
            onClick={() => decide("essential")}
            className="px-4 py-1.5 rounded-full text-sm transition-colors"
            style={{
              border: "1px solid rgba(44, 90, 140, 0.40)",
              color: "#000000",
              background: "transparent",
            }}
          >
            Essential only
          </button>
        </div>
      </div>
    </div>
  );
}
